import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Filter,
  X,
  Eye,
  Edit,
  Trash2,
  KeyRound,
  User,
  Users,
  Download,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import api from "@/services/api";
import AddEmployeeModal from "@/components/AddEmployeeModal";
import EditEmployeeModal from "@/components/EditEmployeeModal";
import EmployeeProfileModal from "@/components/EmployeeProfileModal";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

interface Employee {
  id: number;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  department: string;
  position: string;
  employmentStatus: "active" | "inactive" | "terminated";
  employmentType: "full_time" | "part_time" | "contract";
  baseSalary: string;
  salaryCurrency: "USD" | "CDF";
  hireDate: string;
}

interface EmployeesResponse {
  employees: Employee[];
  pagination: { total: number };
}

interface Filters {
  department: string;
  employmentStatus: string;
  employmentType: string;
}

const Employees: React.FC = () => {
  const { user } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [filters, setFilters] = useState<Filters>({
    department: "",
    employmentStatus: "",
    employmentType: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage] = useState(1);

  const canManageEmployees =
    user?.role === "admin" || user?.role === "hr_manager" || user?.role === "hr_staff";

  const { data, isLoading, refetch } = useQuery<EmployeesResponse>({
    queryKey: ["employees", currentPage, searchTerm, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "50",
        ...(searchTerm && { search: searchTerm }),
        ...(filters.department && { department: filters.department }),
        ...(filters.employmentStatus && { employmentStatus: filters.employmentStatus }),
        ...(filters.employmentType && { employmentType: filters.employmentType }),
      });
      const response = await api.get(`/employees?${params}`);
      return response.data.data;
    },
    enabled: canManageEmployees,
  });

  const departments = useMemo(() => {
    if (!data?.employees) return [];
    return Array.from(new Set(data.employees.map((e) => e.department))).filter(Boolean);
  }, [data?.employees]);

  const statusCounts = useMemo(() => {
    if (!data?.employees) return { active: 0, inactive: 0, terminated: 0 };
    return data.employees.reduce(
      (acc, e) => {
        acc[e.employmentStatus] = (acc[e.employmentStatus] || 0) + 1;
        return acc;
      },
      { active: 0, inactive: 0, terminated: 0 } as Record<string, number>
    );
  }, [data?.employees]);

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const handleProfile = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowProfileModal(true);
  };

  const handleViewCredentials = async (userId: number) => {
    try {
      const response = await api.get(`/admin/users/${userId}/credentials`);
      const creds = response.data.data;
      Swal.fire({
        title: "User Credentials",
        html: `
          <div style="text-align:left;font-size:14px;">
            <p style="margin-bottom:8px;"><strong>Temp Password:</strong> <code style="background:#f1f5f9;padding:2px 8px;border-radius:4px;">${creds.tempPassword || "N/A"}</code></p>
            <p><strong>Expires:</strong> ${new Date(creds.expiresAt).toLocaleString()}</p>
          </div>
        `,
        icon: "info",
        confirmButtonColor: "#4f46e5",
        confirmButtonText: "OK",
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "No valid credentials found");
    }
  };

  const handleDelete = async (employee: Employee) => {
    const result = await Swal.fire({
      title: "Terminate Employee?",
      text: `Are you sure you want to terminate ${employee.firstName} ${employee.lastName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, terminate",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/employees/${employee.id}`);
        toast.success("Employee terminated successfully");
        refetch();
      } catch {
        toast.error("Failed to terminate employee");
      }
    }
  };

  const clearFilters = () =>
    setFilters({ department: "", employmentStatus: "", employmentType: "" });

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string; dot: string }> = {
      active: {
        bg: "bg-emerald-50 dark:bg-emerald-900/20",
        text: "text-emerald-700 dark:text-emerald-400",
        dot: "bg-emerald-500",
      },
      inactive: {
        bg: "bg-amber-50 dark:bg-amber-900/20",
        text: "text-amber-700 dark:text-amber-400",
        dot: "bg-amber-500",
      },
      terminated: {
        bg: "bg-red-50 dark:bg-red-900/20",
        text: "text-red-700 dark:text-red-400",
        dot: "bg-red-500",
      },
    };
    const s = config[status] || config.inactive;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${s.bg} ${s.text}`}>
        <div className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      full_time: "Full Time",
      part_time: "Part Time",
      contract: "Contract",
    };
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400">
        {labels[type] || type}
      </span>
    );
  };

  if (!canManageEmployees) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center mb-3">
            <User className="text-gray-400 h-6 w-6" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Access Denied</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            You don't have permission to view employee data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={22} className="text-indigo-600" />
            Employees
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Manage your organization's workforce
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <Download size={15} />
            Export
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2"
          >
            <Plus size={16} />
            Add Employee
          </button>
        </div>
      </div>

      {/* Stats Mini Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
          <p className="text-lg font-bold text-emerald-600 mt-0.5">{statusCounts.active}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Inactive</p>
          <p className="text-lg font-bold text-amber-600 mt-0.5">{statusCounts.inactive}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
            {data?.pagination?.total || 0}
          </p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card">
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by name, email, or employee number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                showFilters
                  ? "border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
                  : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter size={14} />
              Filters
              {Object.values(filters).some(Boolean) && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-indigo-600 text-white rounded-full font-bold">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Department</label>
                  <select
                    value={filters.department}
                    onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Departments</option>
                    {departments.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                  <select
                    value={filters.employmentStatus}
                    onChange={(e) => setFilters((f) => ({ ...f, employmentStatus: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="terminated">Terminated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Type</label>
                  <select
                    value={filters.employmentType}
                    onChange={(e) => setFilters((f) => ({ ...f, employmentType: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
              </div>
              {Object.values(filters).some(Boolean) && (
                <div className="flex justify-end mt-3">
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                  >
                    <X size={12} /> Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        {!isLoading && (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="pl-5">Employee</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Salary</th>
                  <th className="text-right pr-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.employees?.map((emp) => (
                  <tr key={emp.id}>
                    <td className="pl-5">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-indigo-600 flex items-center justify-center font-semibold text-white text-xs flex-shrink-0">
                          {emp.firstName[0]}{emp.lastName[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {emp.firstName} {emp.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            #{emp.employeeNumber}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="font-medium">{emp.department}</td>
                    <td>{emp.position}</td>
                    <td>{getTypeBadge(emp.employmentType)}</td>
                    <td>{getStatusBadge(emp.employmentStatus)}</td>
                    <td className="font-semibold text-gray-900 dark:text-white">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: emp.salaryCurrency,
                        minimumFractionDigits: 0,
                      }).format(parseFloat(emp.baseSalary))}
                    </td>
                    <td className="text-right pr-5">
                      <div className="flex items-center justify-end gap-1">
                        {[
                          { icon: <KeyRound size={14} />, action: () => handleViewCredentials(emp.id), title: "Credentials", hoverColor: "hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20" },
                          { icon: <Eye size={14} />, action: () => handleProfile(emp), title: "View", hoverColor: "hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20" },
                          { icon: <Edit size={14} />, action: () => handleEdit(emp), title: "Edit", hoverColor: "hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20" },
                          { icon: <Trash2 size={14} />, action: () => handleDelete(emp), title: "Terminate", hoverColor: "hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" },
                        ].map((btn, i) => (
                          <button
                            key={i}
                            onClick={btn.action}
                            className={`p-1.5 rounded-md text-gray-400 transition-all ${btn.hoverColor}`}
                            title={btn.title}
                          >
                            {btn.icon}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(!data?.employees || data.employees.length === 0) && (
              <div className="text-center py-16">
                <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center mb-3">
                  <Users className="text-gray-400 h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  No employees found
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {searchTerm || Object.values(filters).some(Boolean)
                    ? "Try adjusting your search or filters"
                    : "Get started by adding your first employee"}
                </p>
                {!searchTerm && !Object.values(filters).some(Boolean) && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Plus size={14} /> Add First Employee
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {isLoading && (
          <div className="text-center py-16">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading employees...</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddEmployeeModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
            toast.success("Employee added successfully!");
          }}
        />
      )}
      {showEditModal && selectedEmployee && (
        <EditEmployeeModal
          employee={selectedEmployee}
          onClose={() => { setShowEditModal(false); setSelectedEmployee(null); }}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
            toast.success("Employee updated successfully!");
          }}
        />
      )}
      {showProfileModal && selectedEmployee && (
        <EmployeeProfileModal
          employee={selectedEmployee}
          onClose={() => { setShowProfileModal(false); setSelectedEmployee(null); }}
        />
      )}
    </div>
  );
};

export default Employees;
